import type { PageSetup } from '../model';
import type { Annotation, DrawingDocument } from '../model';
import type { ChemMoleculeV1Document } from './moleculeProjection.ts';
import type { ChemIsomerV1Document } from './isomerDocument.ts';
import type { ParticipantCoordinateSet, ParticipantDepiction } from '../chemistry/reactionParticipant.ts';
import { toChemIsomerV1 } from './isomerDocument.ts';
import { createEmptyDrawingDocument } from './drawingDocumentFactory.ts';

// #147: the client-side counterpart to the server's DrawingStructureTransform (#120) --
// projects the live editor's still-v10-shaped DrawingDocument into chem-drawing.v11's
// `moleculeDrawing` shape (an optional isomerRef and independent canvas objects)
// and back. graphDrawing.ts's internal model is unchanged by #144's decision (3); only this save
// boundary is new.

export interface MoleculeDrawingIsomerRef {
  readonly isomerId: string;
  readonly isomerRevision: number;
  readonly depiction: ParticipantDepiction;
  readonly annotations: readonly Annotation[];
}

export interface MoleculeDrawingDocument {
  readonly schemaVersion: 'chem-drawing.v11';
  // Legacy drawing collection discriminator; the drawing itself need not contain chemistry.
  readonly kind: 'molecule';
  readonly isomerRefs: readonly MoleculeDrawingIsomerRef[];
  readonly canvas?: {
    readonly width: number;
    readonly height: number;
    readonly annotations: readonly Annotation[];
  };
  // #772 -- the drawing's page (absent before #772: the default Letter applies).
  readonly page?: PageSetup;
}

/** Rekeys the live document's own depiction (structureId/structureRevision) into the v11
 *  isomerRef shape (isomerId/isomerRevision) -- unlike reactionParticipant.ts's
 *  buildIsomerDepictionPayload, this reuses the depiction the user actually drew rather than
 *  regenerating coordinates via RDKit, since the live editor already has real coordinates. */
/** #951: hydrogens drawn as atoms that the saved Molecule holds as implicit (structureIdRemap.ts). */
export type DrawnHydrogens = NonNullable<ParticipantDepiction['drawnHydrogens']>;

export function toIsomerRefDepiction(
  document: DrawingDocument,
  isomerId: string,
  isomerRevision: number,
  drawnHydrogens: DrawnHydrogens = {}
): ParticipantDepiction {
  const { depiction } = document;
  const coordinateSet: ParticipantCoordinateSet = {
    id: depiction.coordinateSet.id,
    kind: depiction.coordinateSet.kind,
    isomerId,
    isomerRevision,
    dimensions: depiction.coordinateSet.dimensions,
    units: depiction.coordinateSet.units,
    width: depiction.coordinateSet.width,
    height: depiction.coordinateSet.height,
    coordinates: depiction.coordinateSet.coordinates,
    ...(depiction.coordinateSet.depths ? { depths: depiction.coordinateSet.depths } : {})
  };
  return {
    id: depiction.id,
    revision: depiction.revision,
    isomerId,
    isomerRevision,
    coordinateSet,
    atomLabels: depiction.atomLabels,
    bondStyles: depiction.bondStyles,
    bondLengthOverrides: depiction.bondLengthOverrides,
    // #868 -- per-atom / per-bond appearance travels with the depiction both ways.
    ...(depiction.cycleFills ? { cycleFills: depiction.cycleFills } : {}),
    ...(depiction.atomAppearance ? { atomAppearance: depiction.atomAppearance } : {}),
    // #736 step 2 -- groups drawn as their labels travel with the depiction both ways.
    ...(depiction.contractedGroups ? { contractedGroups: depiction.contractedGroups } : {}),
    ...(depiction.bondAppearance ? { bondAppearance: depiction.bondAppearance } : {}),
    ...hydrogenBondsOf(document),
    ...agosticBondsOf(document),
    ...(Object.keys(drawnHydrogens).length ? { drawnHydrogens } : {})
  };
}

/** ADR 0022 D4: the paper's hydrogen bonds, which toChemMoleculeV1 leaves out of the molecule. */
function hydrogenBondsOf(document: DrawingDocument): Pick<ParticipantDepiction, 'hydrogenBonds'> {
  const edges = document.structure.graph.edges.filter((edge) => document.structure.bonds[edge.id]?.type === 'hydrogen');
  return edges.length
    ? {
        hydrogenBonds: Object.fromEntries(edges.map((edge) => [edge.id, { source: edge.source, target: edge.target }]))
      }
    : {};
}

/** ADR 0026 D6: the paper's agostic interactions, likewise left out of the molecule. */
function agosticBondsOf(document: DrawingDocument): Pick<ParticipantDepiction, 'agosticBonds'> {
  const edges = document.structure.graph.edges.filter((edge) => document.structure.bonds[edge.id]?.type === 'agostic');
  return edges.length
    ? {
        agosticBonds: Object.fromEntries(
          edges.map((edge) => [edge.id, { hydrogenAtomId: edge.source, metalAtomId: edge.target }])
        )
      }
    : {};
}

export function buildMoleculeDrawingDocument(
  document: DrawingDocument,
  isomerId: string | null,
  isomerRevision: number,
  drawnHydrogens: DrawnHydrogens = {}
): MoleculeDrawingDocument {
  if (document.structure.graph.nodes.length === 0) {
    return {
      schemaVersion: 'chem-drawing.v11',
      kind: 'molecule',
      isomerRefs: [],
      canvas: {
        width: document.depiction.coordinateSet.width,
        height: document.depiction.coordinateSet.height,
        annotations: document.annotations
      },
      ...(document.page ? { page: document.page } : {})
    };
  }
  if (!isomerId) throw new Error('A drawing with chemistry requires an Isomer reference.');
  return {
    schemaVersion: 'chem-drawing.v11',
    kind: 'molecule',
    isomerRefs: [
      {
        isomerId,
        isomerRevision,
        depiction: toIsomerRefDepiction(document, isomerId, isomerRevision, drawnHydrogens),
        annotations: document.annotations
      }
    ],
    // #772 -- the page travels with the drawing (absent before #772: the default Letter applies).
    ...(document.page ? { page: document.page } : {})
  };
}

/** Builds the chem-isomer.v1 document to POST, given the Molecule it will reference -- reuses
 *  toChemIsomerV1's stereochemistry/cip projection, just overriding its placeholder
 *  moleculeId/moleculeRevision (self-referential, since nothing calls toChemIsomerV1 with a real
 *  Molecule id yet) with the real ones. */
export function toIsomerCreatePayload(
  document: DrawingDocument,
  moleculeId: string,
  moleculeRevision: number,
  drawnHydrogens: DrawnHydrogens = {}
): ChemIsomerV1Document {
  const isomer = toChemIsomerV1(document);
  return {
    ...isomer,
    moleculeId,
    moleculeRevision,
    stereochemistry: Object.keys(drawnHydrogens).length
      ? stereoOnMolecule(document, isomer.stereochemistry, drawnHydrogens)
      : isomer.stereochemistry
  };
}

/**
 * #951: an Isomer names only its Molecule's atoms. A drawn hydrogen the Molecule holds as implicit
 * is that implicit hydrogen as a tetrahedral ligand. As a double bond's reference atom it gives way
 * to the other neighbour on its side, which flips cis and trans; with no other neighbour there is
 * no cis/trans to keep.
 */
function stereoOnMolecule(
  document: DrawingDocument,
  stereochemistry: ChemIsomerV1Document['stereochemistry'],
  drawnHydrogens: DrawnHydrogens
): ChemIsomerV1Document['stereochemistry'] {
  const drawn = (id: string) => id in drawnHydrogens;
  const { edges } = document.structure.graph;
  const neighbours = (atomId: string) =>
    edges.flatMap((edge) => (edge.source === atomId ? [edge.target] : edge.target === atomId ? [edge.source] : []));
  const tetrahedral = Object.fromEntries(
    Object.entries(stereochemistry.tetrahedral).map(([centre, stereo]) => [
      centre,
      {
        ...stereo,
        ligands: stereo.ligands.map((ligand) =>
          'atomId' in ligand && drawn(ligand.atomId) ? { implicitHydrogen: true as const } : ligand
        ) as unknown as typeof stereo.ligands
      }
    ])
  );
  const doubleBonds: Record<string, (typeof stereochemistry.doubleBonds)[string]> = {};
  for (const [bondId, stereo] of Object.entries(stereochemistry.doubleBonds)) {
    const edge = edges.find((candidate) => candidate.id === bondId);
    if (!edge) continue;
    let references = [...stereo.referenceAtomIds] as [string, string];
    let configuration = stereo.relativeConfiguration;
    let kept = true;
    references = references.map((reference) => {
      if (!drawn(reference)) return reference;
      const end = neighbours(reference).find((id) => id === edge.source || id === edge.target);
      const other = end === edge.source ? edge.target : edge.source;
      const replacement = end
        ? neighbours(end).find((id) => id !== other && id !== reference && !drawn(id))
        : undefined;
      if (!replacement) {
        kept = false;
        return reference;
      }
      configuration = configuration === 'cis' ? 'trans' : 'cis';
      return replacement;
    }) as [string, string];
    if (kept) doubleBonds[bondId] = { ...stereo, referenceAtomIds: references, relativeConfiguration: configuration };
  }
  return { ...stereochemistry, tetrahedral, doubleBonds };
}

// The Molecule create payload needs no equivalent builder: moleculeProjection.ts's
// toChemMoleculeV1(document) is already exactly the right shape to POST as-is. Its own id/revision
// fields are schema-required but semantically unused by the server (MoleculeService.create mints
// its own UUID and never reads the body's "id"), so the placeholder self-referential value is
// fine as-is -- no override needed, unlike toIsomerCreatePayload above.

/** The inverse of buildMoleculeDrawingDocument/toIsomerCreatePayload/toChemMoleculeV1 combined:
 *  reassembles the exact v10 DrawingDocument shape the editor's internal model expects from a
 *  fetched Molecule + Isomer + molecule-drawing container -- the client-side mirror of the
 *  server's DrawingStructureTransform#reconstructV10Document. */
export function reconstructDrawingDocument(
  moleculeDrawing: MoleculeDrawingDocument,
  molecule?: ChemMoleculeV1Document,
  isomer?: ChemIsomerV1Document
): DrawingDocument {
  const [ref] = moleculeDrawing.isomerRefs;
  if (!ref) {
    const { canvas } = moleculeDrawing;
    if (!canvas) throw new Error('A drawing without chemistry requires a canvas.');
    const empty = createEmptyDrawingDocument(canvas);
    return {
      ...empty,
      annotations: canvas.annotations,
      ...(moleculeDrawing.page ? { page: moleculeDrawing.page } : {})
    };
  }
  if (!molecule || !isomer) throw new Error('The referenced chemistry is required.');
  const { isomerId, isomerRevision } = ref;

  const { depiction } = ref;
  const { coordinateSet } = depiction;
  const reconstructedDepiction: DrawingDocument['depiction'] = {
    id: depiction.id,
    revision: depiction.revision,
    structureId: isomerId,
    structureRevision: isomerRevision,
    coordinateSet: {
      id: coordinateSet.id,
      kind: coordinateSet.kind,
      structureId: isomerId,
      structureRevision: isomerRevision,
      dimensions: coordinateSet.dimensions,
      units: coordinateSet.units,
      width: coordinateSet.width,
      height: coordinateSet.height,
      coordinates: coordinateSet.coordinates,
      ...(coordinateSet.depths ? { depths: coordinateSet.depths } : {})
    },
    atomLabels: depiction.atomLabels,
    bondStyles: depiction.bondStyles,
    bondLengthOverrides: depiction.bondLengthOverrides,
    // #868 -- per-atom / per-bond appearance travels with the depiction both ways.
    ...(depiction.cycleFills ? { cycleFills: depiction.cycleFills } : {}),
    ...(depiction.atomAppearance ? { atomAppearance: depiction.atomAppearance } : {}),
    // #736 step 2 -- groups drawn as their labels travel with the depiction both ways.
    ...(depiction.contractedGroups ? { contractedGroups: depiction.contractedGroups } : {}),
    ...(depiction.bondAppearance ? { bondAppearance: depiction.bondAppearance } : {})
  };
  // ADR 0022 D4: the hydrogen bonds go back onto the paper as bonds of order 0.
  const hydrogenBonds = Object.entries(depiction.hydrogenBonds ?? {}).filter(
    ([, bond]) => bond.source in molecule.atoms && bond.target in molecule.atoms
  );

  // ADR 0026 D6: the agostic interactions go back onto the paper as order-0 edges from the H.
  const agosticBonds = Object.entries(depiction.agosticBonds ?? {}).filter(
    ([, bond]) => bond.hydrogenAtomId in molecule.atoms && bond.metalAtomId in molecule.atoms
  );
  // ADR 0026 D1: the molecule's hapto bonds go onto the paper as edges from the metal.
  const haptoBonds = Object.entries(molecule.haptoBonds ?? {}).filter(
    ([, bond]) => bond.metalAtomId in molecule.atoms && bond.ligandAtomIds.every((id) => id in molecule.atoms)
  );

  const derivedPropertySets: DrawingDocument['derivedPropertySets'] = [
    ...molecule.derivedPropertySets.map((set) => {
      const { moleculeId: _moleculeId, moleculeRevision: _moleculeRevision, ...rest } = set;
      return { ...rest, structureId: isomerId, structureRevision: isomerRevision };
    }),
    ...isomer.derivedPropertySets.map((set) => {
      const { isomerId: _isomerId, isomerRevision: _isomerRevision, ...rest } = set;
      return { ...rest, structureId: isomerId, structureRevision: isomerRevision };
    })
  ] as DrawingDocument['derivedPropertySets'];

  const reconstructed: DrawingDocument = {
    schemaVersion: 'chem-drawing.v10',
    structure: {
      id: isomerId,
      revision: isomerRevision,
      graph: {
        id: isomerId,
        revision: isomerRevision,
        nodes: molecule.graph.nodes,
        edges:
          hydrogenBonds.length || haptoBonds.length || agosticBonds.length
            ? [
                ...molecule.graph.edges,
                ...hydrogenBonds.map(([id, bond]) => ({ id, ...bond })),
                ...agosticBonds.map(([id, bond]) => ({ id, source: bond.hydrogenAtomId, target: bond.metalAtomId })),
                ...haptoBonds.map(([id, bond]) => ({ id, source: bond.metalAtomId, target: bond.ligandAtomIds[0] }))
              ]
            : molecule.graph.edges
      },
      atoms: molecule.atoms,
      bonds:
        hydrogenBonds.length || haptoBonds.length || agosticBonds.length
          ? {
              ...molecule.bonds,
              ...Object.fromEntries(
                hydrogenBonds.map(([id]) => [id, { order: 0 as const, type: 'hydrogen' as const }])
              ),
              ...Object.fromEntries(agosticBonds.map(([id]) => [id, { order: 0 as const, type: 'agostic' as const }])),
              ...Object.fromEntries(
                haptoBonds.map(([id, bond]) => [
                  id,
                  { order: 0 as const, type: 'hapto' as const, ligandAtomIds: bond.ligandAtomIds }
                ])
              )
            }
          : molecule.bonds,
      formalCharges: molecule.formalCharges,
      stereochemistry: isomer.stereochemistry
    },
    depiction: reconstructedDepiction,
    annotations: [...ref.annotations, ...(moleculeDrawing.canvas?.annotations ?? [])],
    derivedPropertySets,
    ...(moleculeDrawing.page ? { page: moleculeDrawing.page } : {})
  };
  return withDrawnHydrogens(reconstructed, depiction.drawnHydrogens ?? {}, molecule);
}

/** #951: puts the hydrogens drawn as atoms -- which the Molecule holds as implicit -- back on the paper. */
function withDrawnHydrogens(
  document: DrawingDocument,
  drawnHydrogens: DrawnHydrogens,
  molecule: ChemMoleculeV1Document
): DrawingDocument {
  const drawn = Object.entries(drawnHydrogens).filter(
    ([hydrogen, { atomId }]) => atomId in molecule.atoms && !(hydrogen in molecule.atoms)
  );
  if (!drawn.length) return document;
  const { structure } = document;
  return {
    ...document,
    structure: {
      ...structure,
      graph: {
        ...structure.graph,
        nodes: [...structure.graph.nodes, ...drawn.map(([hydrogen]) => ({ id: hydrogen }))],
        edges: [
          ...structure.graph.edges,
          ...drawn.map(([hydrogen, { atomId, bondId }]) => ({ id: bondId, source: atomId, target: hydrogen }))
        ]
      },
      atoms: { ...structure.atoms, ...Object.fromEntries(drawn.map(([hydrogen]) => [hydrogen, { atomicNumber: 1 }])) },
      bonds: {
        ...structure.bonds,
        ...Object.fromEntries(drawn.map(([, { bondId }]) => [bondId, { order: 1 as const, type: 'covalent' as const }]))
      },
      formalCharges: { ...structure.formalCharges, ...Object.fromEntries(drawn.map(([hydrogen]) => [hydrogen, 0])) }
    }
  };
}

/** Stereochemistry-and-cip fingerprint, revision-independent -- the isomer-side counterpart to
 *  moleculeProjection.ts's moleculeSourceKey. Together, comparing both keys against the
 *  last-saved document is what replaces the server's decide() reuse-on-save logic (#144 decision
 *  2): unchanged on both means nothing needs minting; unchanged constitution but changed
 *  stereochemistry means mint a new Isomer only; a changed constitution means mint both (the
 *  Molecule mint is itself a no-op row-wise if corpus-wide dedup, #141/#143, finds a match). */
export function isomerSourceKey(document: DrawingDocument): string {
  const isomer = toChemIsomerV1(document);
  return JSON.stringify({
    stereochemistry: isomer.stereochemistry,
    derivedPropertySets: isomer.derivedPropertySets.map(({ isomerId: _i, isomerRevision: _r, ...rest }) => rest)
  });
}
